import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  Upload, Folder, FolderPlus, Image, Video, FileText, Music, 
  Trash2, Download, Search, Grid, List, X, ExternalLink, File, Users, Link, AlertCircle, Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { mediaAPI } from '../lib/api';
import api from '../lib/api';

const FILE_TYPE_ICONS = {
  image: Image,
  video: Video,
  document: FileText,
  audio: Music,
  other: File
};

const FILE_TYPE_COLORS = {
  image: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  video: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  document: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  audio: 'bg-green-500/20 text-green-400 border-green-500/30',
  other: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const MediaDrop = () => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState(['general']);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedClass, setSelectedClass] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'document',
    description: '',
    folder: 'general',
    class_ids: [],
    is_general: false,
    uploadedFile: null // For direct file uploads
  });
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [filesRes, foldersRes, classesRes] = await Promise.all([
        mediaAPI.getAll(),
        mediaAPI.getFolders(),
        api.get('/classes')
      ]);
      setFiles(filesRes.data || []);
      setFolders(foldersRes.data?.length ? foldersRes.data : ['general']);
      setClasses(classesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      setLoadError('Failed to load media files. Please refresh the page.');
      toast.error('Failed to load media files');
    } finally {
      setLoading(false);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Check file size (100MB max for video, 10MB for others)
    const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }
    
    // Auto-detect file type
    let detectedType = 'document';
    if (file.type.startsWith('image/')) detectedType = 'image';
    else if (file.type.startsWith('video/')) detectedType = 'video';
    else if (file.type.startsWith('audio/')) detectedType = 'audio';
    
    setUploadForm({
      ...uploadForm,
      file_name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      file_type: detectedType,
      uploadedFile: file,
      file_url: '' // Clear URL when file is selected
    });
    
    setUploadModalOpen(true);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    // Validate: need either a file or a URL
    const hasFile = uploadForm.uploadedFile !== null;
    const hasUrl = uploadForm.file_url?.trim().length > 0;
    
    if (!uploadForm.file_name?.trim()) {
      toast.error('Please provide a file name');
      return;
    }
    
    if (!hasFile && !hasUrl) {
      toast.error('Please upload a file or provide a URL');
      return;
    }

    if (!uploadForm.is_general && uploadForm.class_ids.length === 0) {
      toast.error('Please select at least one class or mark as "Available to all"');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      
      let fileUrl = uploadForm.file_url;
      
      // If we have an uploaded file, upload it first
      if (hasFile) {
        const formData = new FormData();
        formData.append('file', uploadForm.uploadedFile);
        
        // Determine media type for upload
        const mediaType = uploadForm.file_type === 'image' ? 'image' : 
                         uploadForm.file_type === 'video' ? 'video' : 'image';
        formData.append('media_type', mediaType);
        
        const uploadRes = await api.post('/site-media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
        
        fileUrl = uploadRes.data.url;
      }
      
      // Now save the media record
      await mediaAPI.upload({
        file_name: uploadForm.file_name,
        file_url: fileUrl,
        file_type: uploadForm.file_type,
        description: uploadForm.description,
        folder: uploadForm.folder,
        class_ids: uploadForm.is_general ? [] : uploadForm.class_ids,
        is_general: uploadForm.is_general
      });
      
      toast.success('File added successfully!');
      setUploadModalOpen(false);
      setUploadForm({
        file_name: '',
        file_url: '',
        file_type: 'document',
        description: '',
        folder: 'general',
        class_ids: [],
        is_general: false,
        uploadedFile: null
      });
      setUploadProgress(0);
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to add file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) return;
    
    try {
      await mediaAPI.delete(fileId);
      toast.success('File deleted');
      fetchData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    const sanitized = newFolderName.toLowerCase().replace(/[^a-z0-9-_]/g, '-');
    if (!folders.includes(sanitized)) {
      setFolders([...folders, sanitized]);
    }
    setUploadForm({ ...uploadForm, folder: sanitized });
    setNewFolderName('');
    setNewFolderModalOpen(false);
    toast.success(`Folder "${sanitized}" ready to use`);
  };

  const handleDownload = async (file) => {
    try {
      toast.info(`Downloading ${file.file_name}...`);
      
      // Try to fetch and download the file
      const response = await fetch(file.file_url);
      if (!response.ok) {
        // If fetch fails (CORS), open in new tab for manual download
        window.open(file.file_url, '_blank');
        toast.info('File opened in new tab - save it from there for offline use');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${file.file_name} downloaded! Save it for offline access.`);
    } catch (error) {
      // Fallback: open in new tab
      window.open(file.file_url, '_blank');
      toast.info('File opened in new tab - save it from there for offline use');
    }
  };

  const getFileTypeFromUrl = (url) => {
    const ext = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return 'audio';
    if (['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx'].includes(ext)) return 'document';
    return 'other';
  };

  const filteredFiles = files.filter(file => {
    const matchesFolder = selectedFolder === 'all' || file.folder === selectedFolder;
    const matchesType = selectedType === 'all' || file.file_type === selectedType;
    const matchesClass = selectedClass === 'all' || file.is_general || file.class_ids?.includes(selectedClass);
    const matchesSearch = !searchQuery || 
      file.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFolder && matchesType && matchesClass && matchesSearch;
  });

  const getClassNames = (classIds) => {
    if (!classIds || classIds.length === 0) return null;
    return classIds.map(id => classes.find(c => c.id === id)?.name).filter(Boolean).join(', ');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="media-drop-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-purple-600 rounded-xl flex items-center justify-center">
                  <Folder className="w-6 h-6 text-white" />
                </div>
                Media Drop
              </h1>
              <p className="text-white/60 mt-2">Share music, videos, and documents with the team</p>
            </div>
            <Button 
              onClick={() => setUploadModalOpen(true)}
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold"
              data-testid="upload-media-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Media
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white w-64"
                data-testid="search-media"
              />
            </div>
            
            {/* Folder Filter */}
            <Select value={selectedFolder} onValueChange={setSelectedFolder}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="folder-filter">
                <Folder className="w-4 h-4 mr-2 text-white/60" />
                <SelectValue placeholder="All Folders" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Folders</SelectItem>
                {folders.map(folder => (
                  <SelectItem key={folder} value={folder} className="text-white hover:bg-white/10 capitalize">
                    {folder}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Types</SelectItem>
                <SelectItem value="image" className="text-white hover:bg-white/10">Images</SelectItem>
                <SelectItem value="video" className="text-white hover:bg-white/10">Videos</SelectItem>
                <SelectItem value="audio" className="text-white hover:bg-white/10">Audio</SelectItem>
                <SelectItem value="document" className="text-white hover:bg-white/10">Documents</SelectItem>
              </SelectContent>
            </Select>

            {/* Class Filter */}
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="class-filter">
                <Users className="w-4 h-4 mr-2 text-white/60" />
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10 max-h-60">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Classes</SelectItem>
                {classes.map(cls => (
                  <SelectItem key={cls.id} value={cls.id} className="text-white hover:bg-white/10">
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#FF007F] text-white' : 'text-white/60 hover:text-white'}`}
              data-testid="view-grid"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#FF007F] text-white' : 'text-white/60 hover:text-white'}`}
              data-testid="view-list"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 mt-4 text-sm">
          <span className="text-white/60">{filteredFiles.length} files</span>
          <span className="text-white/40">•</span>
          <span className="text-white/60">{folders.length} folders</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : loadError ? (
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Failed to Load</h3>
              <p className="text-white/60 mb-4">{loadError}</p>
              <Button 
                onClick={fetchData}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredFiles.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-20 text-center">
              <Folder className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No files yet</h3>
              <p className="text-white/60 mb-6">Upload media files to share with your team</p>
              <Button 
                onClick={() => setUploadModalOpen(true)}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add Your First File
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredFiles.map(file => {
              const IconComponent = FILE_TYPE_ICONS[file.file_type] || File;
              return (
                <Card 
                  key={file.id} 
                  className="bg-white/5 border-white/10 hover:border-[#FF007F]/50 transition-all cursor-pointer group overflow-hidden"
                  onClick={() => setPreviewFile(file)}
                  data-testid={`media-file-${file.id}`}
                >
                  <div className="aspect-square relative bg-black/30 flex items-center justify-center">
                    {file.file_type === 'image' ? (
                      <img 
                        src={file.file_url} 
                        alt={file.file_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`${file.file_type === 'image' ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                      <IconComponent className="w-12 h-12 text-white/30" />
                    </div>
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                        className="p-2 bg-green-500/50 rounded-full hover:bg-green-500/70"
                        title="Download for offline"
                      >
                        <Download className="w-4 h-4 text-white" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); window.open(file.file_url, '_blank'); }}
                        className="p-2 bg-white/20 rounded-full hover:bg-white/30"
                        title="Open in browser"
                      >
                        <ExternalLink className="w-4 h-4 text-white" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDelete(file.id, file.file_name); }}
                        className="p-2 bg-red-500/50 rounded-full hover:bg-red-500/70"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-white text-sm font-medium truncate">{file.file_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <Badge className={`text-xs ${FILE_TYPE_COLORS[file.file_type]}`}>
                        {file.file_type}
                      </Badge>
                      <span className="text-white/40 text-xs">{formatDate(file.created_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFiles.map(file => {
              const IconComponent = FILE_TYPE_ICONS[file.file_type] || File;
              return (
                <Card 
                  key={file.id}
                  className="bg-white/5 border-white/10 hover:border-[#FF007F]/50 transition-all"
                  data-testid={`media-file-${file.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${FILE_TYPE_COLORS[file.file_type]}`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{file.file_name}</p>
                      <p className="text-white/50 text-sm truncate">{file.description || 'No description'}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={`text-xs mb-1 ${FILE_TYPE_COLORS[file.file_type]}`}>
                        {file.folder}
                      </Badge>
                      <p className="text-white/40 text-xs">{file.uploaded_by_name}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(file)}
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                        title="Download for offline"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(file.file_url, '_blank')}
                        className="text-white/60 hover:text-white hover:bg-white/10"
                        title="Open in browser"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(file.id, file.file_name)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={(open) => {
        setUploadModalOpen(open);
        if (!open) {
          setUploadForm({
            file_name: '',
            file_url: '',
            file_type: 'document',
            description: '',
            folder: 'general',
            class_ids: [],
            is_general: false,
            uploadedFile: null
          });
          setUploadProgress(0);
        }
      }}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#FF007F]" />
              Add Media File
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Drag and Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive ? 'border-[#FF007F] bg-[#FF007F]/10' : 'border-white/20 hover:border-white/40'
              } ${uploadForm.uploadedFile ? 'bg-green-500/10 border-green-500/50' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleFileInputChange}
                className="hidden"
                id="file-upload-input"
              />
              
              {uploadForm.uploadedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <File className="w-8 h-8" />
                    <span className="font-medium">{uploadForm.uploadedFile.name}</span>
                  </div>
                  <p className="text-white/50 text-sm">
                    {(uploadForm.uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadForm({ ...uploadForm, uploadedFile: null })}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <label htmlFor="file-upload-input" className="cursor-pointer block">
                  <Upload className="w-10 h-10 mx-auto text-white/40 mb-2" />
                  <p className="text-white/70">Drop files here or click to upload</p>
                  <p className="text-white/40 text-xs mt-1">
                    Video (100MB), Images (10MB), Audio, Documents
                  </p>
                </label>
              )}
            </div>
            
            {/* OR divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-xs">OR paste a link</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            
            {/* External URL */}
            <div>
              <label className="text-sm text-white/70 mb-1 block flex items-center gap-2">
                <Link className="w-4 h-4" />
                External Link (optional)
              </label>
              <Input
                placeholder="https://drive.google.com/... or https://dropbox.com/..."
                value={uploadForm.file_url}
                onChange={(e) => {
                  const url = e.target.value;
                  const detectedType = getFileTypeFromUrl(url);
                  setUploadForm({ 
                    ...uploadForm, 
                    file_url: url,
                    file_type: detectedType !== 'other' ? detectedType : uploadForm.file_type,
                    uploadedFile: null // Clear uploaded file when URL is entered
                  });
                }}
                className="bg-white/5 border-white/10 text-white"
                data-testid="upload-file-url"
                disabled={uploadForm.uploadedFile !== null}
              />
              <p className="text-white/40 text-xs mt-1">
                Paste a link from Dropbox, Google Drive, YouTube, or any public URL
              </p>
            </div>

            {/* File Name */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">File Name *</label>
              <Input
                placeholder="e.g., Dance Routine Music"
                value={uploadForm.file_name}
                onChange={(e) => setUploadForm({ ...uploadForm, file_name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="upload-file-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">File Type</label>
                <Select 
                  value={uploadForm.file_type} 
                  onValueChange={(v) => setUploadForm({ ...uploadForm, file_type: v })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="upload-file-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10">
                    <SelectItem value="image" className="text-white">Image</SelectItem>
                    <SelectItem value="video" className="text-white">Video</SelectItem>
                    <SelectItem value="audio" className="text-white">Audio</SelectItem>
                    <SelectItem value="document" className="text-white">Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Folder</label>
                <div className="flex gap-2">
                  <Select 
                    value={uploadForm.folder} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, folder: v })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white flex-1" data-testid="upload-folder">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      {folders.map(folder => (
                        <SelectItem key={folder} value={folder} className="text-white capitalize">
                          {folder}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setNewFolderModalOpen(true)}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <FolderPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Description (optional)</label>
              <Textarea
                placeholder="Add a description..."
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={2}
                data-testid="upload-description"
              />
            </div>

            {/* Class Selection */}
            <div className="border-t border-white/10 pt-4">
              <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                <Users className="w-4 h-4" />
                Who can access this file? *
              </label>
              
              <div className="flex items-center gap-2 mb-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <Checkbox
                  id="is_general"
                  checked={uploadForm.is_general}
                  onCheckedChange={(checked) => setUploadForm({ 
                    ...uploadForm, 
                    is_general: checked,
                    class_ids: checked ? [] : uploadForm.class_ids 
                  })}
                  className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                />
                <label htmlFor="is_general" className="text-sm text-white cursor-pointer">
                  Available to all students (general resource)
                </label>
              </div>

              {!uploadForm.is_general && (
                <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-black/20 rounded-lg">
                  {classes.length === 0 ? (
                    <p className="text-white/50 text-sm text-center py-2">No classes available</p>
                  ) : (
                    classes.map(cls => (
                      <div key={cls.id} className="flex items-center gap-2 p-2 rounded hover:bg-white/5">
                        <Checkbox
                          id={`class-${cls.id}`}
                          checked={uploadForm.class_ids.includes(cls.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setUploadForm({ ...uploadForm, class_ids: [...uploadForm.class_ids, cls.id] });
                            } else {
                              setUploadForm({ ...uploadForm, class_ids: uploadForm.class_ids.filter(id => id !== cls.id) });
                            }
                          }}
                          className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                        />
                        <label htmlFor={`class-${cls.id}`} className="text-sm text-white cursor-pointer flex-1">
                          {cls.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}
              <p className="text-white/40 text-xs mt-2">
                Students will see this in their Practice Materials section
              </p>
            </div>
            
            {/* Upload Progress */}
            {uploading && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#FF007F] transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-white/50 text-xs text-center">{uploadProgress}% uploaded</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || (!uploadForm.file_name?.trim()) || (!uploadForm.uploadedFile && !uploadForm.file_url?.trim())}
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
              data-testid="submit-upload"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : 'Add File'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Modal */}
      <Dialog open={newFolderModalOpen} onOpenChange={setNewFolderModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-[#FF007F]" />
              Create Folder
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="new-folder-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} className="bg-[#FF007F] hover:bg-[#D6006A] text-white" data-testid="create-folder-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-3xl">
          {previewFile && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white truncate pr-8">{previewFile.file_name}</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                {previewFile.file_type === 'image' && (
                  <img 
                    src={previewFile.file_url} 
                    alt={previewFile.file_name}
                    className="max-h-96 w-full object-contain rounded-lg"
                  />
                )}
                {previewFile.file_type === 'video' && (
                  <video 
                    src={previewFile.file_url} 
                    controls
                    className="max-h-96 w-full rounded-lg"
                  />
                )}
                {previewFile.file_type === 'audio' && (
                  <div className="bg-black/30 p-8 rounded-lg">
                    <audio src={previewFile.file_url} controls className="w-full" />
                  </div>
                )}
                {previewFile.file_type === 'document' && (
                  <div className="bg-black/30 p-8 rounded-lg text-center">
                    <FileText className="w-16 h-16 mx-auto text-white/30 mb-4" />
                    <p className="text-white/60">Click below to open document</p>
                  </div>
                )}
                {previewFile.description && (
                  <p className="text-white/70 mt-4">{previewFile.description}</p>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10 text-sm">
                  <div className="text-white/50">
                    <span>Uploaded by {previewFile.uploaded_by_name}</span>
                    <span className="mx-2">•</span>
                    <span>{formatDate(previewFile.created_at)}</span>
                  </div>
                  <Badge className={FILE_TYPE_COLORS[previewFile.file_type]}>
                    {previewFile.folder}
                  </Badge>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setPreviewFile(null)} 
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  Close
                </Button>
                <Button 
                  onClick={() => handleDownload(previewFile)}
                  variant="outline"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download for Offline
                </Button>
                <Button 
                  onClick={() => window.open(previewFile.file_url, '_blank')}
                  className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open File
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaDrop;
